import { z } from 'zod';

/* ---------- Usage and plan (SPEC §18.1, api/openapi.yaml UsagePlanState / UsageReport) ---------- */
/*
 * Per-org plan, the four meters and the month's metered usage. A level or unit the console does not know falls back to
 * `ok` / `builds` so a newer API never breaks the page; limits are `null` for "unlimited".
 */

export const MeterId = z.enum(['minutes', 'builds', 'storage', 'concurrency']);
export type MeterId = z.infer<typeof MeterId>;
export const MeterLevel = z.enum(['ok', 'warn', 'exceeded', 'unlimited']);
export type MeterLevel = z.infer<typeof MeterLevel>;

export const UsageMeter = z.object({
  id: MeterId,
  unit: z.enum(['min', 'builds', 'bytes']).catch('builds'),
  used: z.number(),
  limit: z.number().nullable(),
  percent: z.number().nullable(),
  level: MeterLevel.catch('ok'),
});
export type UsageMeter = z.infer<typeof UsageMeter>;

export const UsagePlan = z.object({
  name: z.string(),
  source: z.enum(['org', 'default']).catch('default'),
  monthlyBuildMinutes: z.number().nullable(),
  monthlyBuilds: z.number().nullable(),
  maxConcurrentBuilds: z.number().nullable(),
  artifactStorageGb: z.number().nullable(),
  overQuota: z.enum(['block', 'warn']).catch('warn'),
  allowUntil: z.string().nullable(),
  allowReason: z.string().nullable(),
});
export type UsagePlan = z.infer<typeof UsagePlan>;

export const UsageLimits = z.object({
  month: z.string(),
  generatedAt: z.string(),
  plan: UsagePlan,
  meters: z.object({ minutes: UsageMeter, builds: UsageMeter, storage: UsageMeter, concurrency: UsageMeter }),
  level: MeterLevel.catch('ok'),
  exceeded: z.array(MeterId).default([]),
  overrideActive: z.boolean(),
  blocked: z.boolean(),
  resetsAt: z.string(),
});
export type UsageLimits = z.infer<typeof UsageLimits>;

export const UsageRow = z.object({
  builds: z.number(),
  finished: z.number(),
  billableMinutes: z.number(),
  providerSeconds: z.number(),
  queuedSeconds: z.number(),
  artifactBytes: z.number(),
});
export type UsageRow = z.infer<typeof UsageRow>;

export const UsageReport = z.object({
  month: z.string(),
  period: z.object({ start: z.string(), end: z.string() }),
  generatedAt: z.string(),
  totals: UsageRow,
  months: z.array(z.string()),
  byProject: z.array(UsageRow.extend({ projectId: z.string(), project: z.string().nullable() })),
  byApp: z.array(UsageRow.extend({ appId: z.string(), app: z.string().nullable(), projectId: z.string() })),
  byProvider: z.array(UsageRow.extend({ provider: z.string(), providerAccountId: z.string(), providerAccount: z.string().nullable() })),
  byPlatform: z.array(UsageRow.extend({ platform: z.string() })),
});
export type UsageReport = z.infer<typeof UsageReport>;
